import type { ReactNode } from 'react';
import { useNavigation, Link } from '../app/navigation';
import { useWorkspace } from '../app/workspace';
import { unresolvedExceptions } from '../domain/engine';
import { Brand } from './Brand';
import { StatusNotice } from './StatusNotice';
import type { Role } from '../domain/types';

type ShellRole = Role | 'sample_user';

const nav = [
  { href: '/close/today', label: '오늘 마침', short: '마침', icon: 'line', roles: ['owner', 'manager', 'sample_user'] },
  { href: '/classes', label: '수업', short: '수업', icon: 'book', roles: ['owner', 'manager', 'instructor', 'desk', 'read_only', 'sample_user'] },
  { href: '/students', label: '학생', short: '학생', icon: 'person', roles: ['owner', 'manager', 'instructor', 'desk', 'read_only', 'sample_user'] },
  { href: '/billing/reconcile', label: '수납', short: '수납', icon: 'won', roles: ['owner', 'manager', 'accounting', 'sample_user'] },
  { href: '/payroll/close', label: '강사', short: '강사', icon: 'team', roles: ['owner', 'manager', 'accounting', 'sample_user'] },
  { href: '/records', label: '기록', short: '기록', icon: 'archive', roles: ['owner', 'manager', 'accounting', 'desk', 'read_only', 'sample_user'] },
] as const;

export function AppShell({ children, title, description, actions }: { children: ReactNode; title: string; description?: string; actions?: ReactNode }) {
  const { pathname } = useNavigation();
  const { state, mode, online, role } = useWorkspace();
  const remaining = state ? unresolvedExceptions(state).length : 0;
  const visibleNav = nav.filter((item) =>
    role ? (item.roles as readonly ShellRole[]).includes(role) : false,
  );
  const needsSetup = pathname !== '/onboarding/setup' && mode === 'user' && state && (state.classes.length === 0 || state.students.length === 0);
  const today = new Intl.DateTimeFormat('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' }).format(new Date());
  return (
    <div className="app-shell">
      <aside className="app-rail">
        <Brand compact />
        <div className="academy-label"><small>{mode === 'sample' ? '데이터 없이 둘러보기' : '운영 중인 학원'}</small><strong>{state?.tenant.name ?? '수업마침'}</strong><span>{today} · 남은 일 {remaining}</span></div>
        <nav aria-label="운영 메뉴">
          {visibleNav.map((item) => <Link key={item.href} href={item.href} className={pathname.startsWith(item.href.split('/').slice(0, 2).join('/')) ? 'is-current' : ''} aria-current={pathname.startsWith(item.href) ? 'page' : undefined}>
            <NavIcon name={item.icon} /><span>{item.label}</span>{item.href === '/close/today' && remaining > 0 ? <b aria-label={`남은 일 ${remaining}건`}>{remaining}</b> : null}
          </Link>)}
        </nav>
        <div className="app-rail__foot">
          <Link href="/support">도움 요청</Link>
          {role === 'owner' || role === 'sample_user' ? <Link href="/settings/data">설정</Link> : null}
          <span className={`connection-state ${online ? 'is-online' : ''}`}><i aria-hidden="true" />{online ? '연결됨' : '오프라인'}</span>
        </div>
      </aside>
      <div className="app-canvas">
        <header className="app-header">
          <div><p>{mode === 'sample' ? '둘러보기 · 저장 기록은 12시간 뒤 삭제' : `${today} · ${state?.tenant.name}`}</p><h1>{title}</h1>{description ? <span>{description}</span> : null}</div>
          {actions ? <div className="app-header__actions">{actions}</div> : null}
        </header>
        <StatusNotice />
        <main className="app-main">{needsSetup ? <aside className="setup-callout"><div><strong>첫 운영일을 만들 준비가 필요합니다.</strong><span>수업과 학생을 등록하면 오늘 출결표와 마감 기록이 실제 데이터로 이어집니다.</span></div><Link href="/onboarding/setup" className="secondary-action">설정 이어가기</Link></aside> : null}{children}</main>
      </div>
      <nav className="mobile-nav" aria-label="모바일 운영 메뉴">
        {visibleNav.slice(0, 5).map((item) => <Link key={item.href} href={item.href} className={pathname.startsWith(item.href.split('/').slice(0, 2).join('/')) ? 'is-current' : ''}><NavIcon name={item.icon} /><span>{item.short}</span>{item.href === '/close/today' && remaining > 0 ? <b>{remaining}</b> : null}</Link>)}
      </nav>
    </div>
  );
}

function NavIcon({ name }: { name: string }) {
  const paths: Record<string, ReactNode> = {
    line: <><path d="M4 6h11M4 12h14M4 18h9" /><path d="M18 4v17" /></>,
    book: <><path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H12v17H7.5A3.5 3.5 0 0 0 4 22V5.5Z" /><path d="M20 5.5A3.5 3.5 0 0 0 16.5 2H12v17h4.5A3.5 3.5 0 0 1 20 22V5.5Z" /></>,
    person: <><circle cx="12" cy="8" r="4" /><path d="M4 21c.8-4 3.5-6 8-6s7.2 2 8 6" /></>,
    won: <><circle cx="12" cy="12" r="9" /><path d="m7 8 2 8 3-7 3 7 2-8M6 12h12" /></>,
    team: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c.6-4 2.6-6 6-6s5.4 2 6 6M14 15c3.6 0 5.8 1.7 6.5 5" /></>,
    archive: <><path d="M4 7h16v14H4zM3 3h18v4H3z" /><path d="M9 11h6" /></>,
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}
